import { SMSSender } from 'modules/graphql/types';
import { AssetDetails } from 'modules/assetManager/contexts/BaseAssetManagerContext';
import { AVATAR_SIZES, AVATAR_SIZE_TYPES } from 'ui/avatar';
import { Image, Person } from 'ui/icons';
import { IS_VALID_EMAIL } from 'utils/validationRegex';
import { StyledAvatar } from './components/ContactCards.styles';
import { ContactCard, ContactCardErrorMessages, ContactCardOption } from './types';

const handlebarsRegex = /([{[]{2}(.+)[}\]]{2})+/gi;
export const isHandlebarsInput = (input?: string) =>
  typeof input === 'string' && input.match(handlebarsRegex) !== null;

export const getContactCardAvatar = (card: ContactCard, size: AVATAR_SIZE_TYPES) => {
  const showPersonSvg = !card?.imageUrl && !card?.displayName;
  const showImageSvg = isHandlebarsInput(card?.imageUrl);

  const getIconSize = (avatarSize: AVATAR_SIZE_TYPES) => {
    switch (avatarSize) {
      case AVATAR_SIZES.SMALL:
        return 12;
      case AVATAR_SIZES.XLARGE:
        return 35;
      case AVATAR_SIZES.MEDIUM:
      default:
        return 16;
    }
  };

  // due to children checks added to the avatar, we must consolidate them as one child otherwise they will be sent as an array and thus defined.
  const children = (
    <>
      {showPersonSvg && !showImageSvg && (
        <Person width={getIconSize(size)} height={getIconSize(size)} />
      )}
      {showImageSvg && !showPersonSvg && (
        <Image width={getIconSize(size)} height={getIconSize(size)} />
      )}
    </>
  );

  return (
    <StyledAvatar
      key={card?.id}
      color={!showImageSvg ? 'grey' : undefined}
      firstName={card?.displayName || ''}
      id={card?.id}
      imgSrc={!showImageSvg ? card?.imageUrl : undefined}
      size={size}
    >
      {(showPersonSvg || showImageSvg) && children}
    </StyledAvatar>
  );
};

/**
 * Formats the contact card options for selecting a contact to add in an SMS template
 */
export const formatContactCardOptions = (contactCards: ContactCard[]): ContactCardOption[] =>
  contactCards.map((card) => {
    const displayName =
      card.displayName.length >= 20 ? `${card.displayName.substring(0, 20)}...` : card.displayName;
    return {
      id: card.id.toString(),
      label: displayName,
      value: card.id,
      Prefix: getContactCardAvatar(card, AVATAR_SIZES.SMALL),
    };
  });

/**
 * Formats the sms sender options for selecting a sender to add in a contact card
 */
export const formatSMSSenderOptions = (smsSenders: SMSSender[]) => {
  return smsSenders.map((sender) => ({
    doubleLine: sender.phoneNumber.toString(),
    id: sender.id.toString(),
    label: sender.name,
    value: sender.id,
  }));
};

export const validateContactCard = (
  contactCard: AssetDetails<ContactCard>,
): ContactCardErrorMessages => {
  const validators = {
    displayName: (v: string) => v && v !== '',
    smsSender: (v: any) => v && v?.id,
    imageUrl: (v: string) => !v || v.startsWith('https://') || isHandlebarsInput(v),
    email: (v: string) => !v || IS_VALID_EMAIL.test(v) || isHandlebarsInput(v),
  };

  const errorMessages = {
    displayName: 'Display name is required',
    smsSender: 'Sender must be selected',
    imageUrl: 'Image URL must start with https:// or use handlebars',
    email: 'Email must be valid format or use handlebars',
  };

  return Object.entries(validators).reduce((errors, [key, validator]) => {
    return {
      ...errors,
      ...(validator(contactCard[key as keyof ContactCard] as never)
        ? null
        : { [key]: errorMessages[key as keyof typeof errorMessages] }),
    };
  }, {});
};
